/**
 * rules domain — Rules & Automations config (doc 01 A6.5, doc 13 T2). Read
 * `rules:read`; mutate `rules:configure`/`rules:manage`. Parameters are typed per
 * rule module — there is NO generic builder (D-item). Severity comes from the
 * engine, never UI-promoted; rules are disabled, never deleted.
 * Types mirror the API's `RuleView`/`EvaluationView`/`AutomationView` views.
 */
import { get, patch, post } from './transport.js';
/**
 * MIRROR of the server's `AUDIENCE_PARAMETER_KEYS`
 * (apps/api/src/domains/rules/schemas.ts) — the parameter keys that NAME AN
 * AUDIENCE. The server REFUSES a personal automation carrying any of them
 * (model rule 1: a personal automation's audience is its owner, structurally),
 * so a builder must not offer them for the personal tier. This list exists so
 * the client's validation mirrors the server schema rather than reinventing it;
 * if the server list changes, this one changes with it.
 */
export const PERSONAL_AUDIENCE_PARAMETER_KEYS = Object.freeze([
    'audience',
    'audienceUserIds',
    'audienceWorkerIds',
    'workerIds',
    'userIds',
    'recipients',
    'recipientIds',
    'notifyUserIds',
    'targetUserIds',
]);
/** `GET /rules` → `{ rules }`. */
export async function list(options) {
    const body = await get('/rules', options);
    return body.rules;
}
/** `GET /rules/automations` → `{ automations }` (config + execution summary). */
export async function automations(options) {
    const body = await get('/rules/automations', options);
    return body.automations;
}
/** `GET /rules/catalog` → `{ catalog }` (WHEN/IF/THEN templates). */
export async function catalog(options) {
    const body = await get('/rules/catalog', options);
    return body.catalog;
}
/**
 * `POST /rules` → `{ rule }` — create ONE tier-scoped automation via the
 * converged create service (`createTierScopedRule`; model rule 4: the manual
 * builder and the Intelligence path both end there).
 */
export async function create(input, options) {
    const body = await post('/rules', input, options);
    return body.rule;
}
/** `GET /rules/:id` → `{ rule }`. */
export async function detail(id, options) {
    const body = await get(`/rules/${id}`, options);
    return body.rule;
}
/** `PATCH /rules/:id` → `{ rule }`. Parameters merge; keys cannot be deleted. */
export async function update(id, input, options) {
    const body = await patch(`/rules/${id}`, input, options);
    return body.rule;
}
/** `POST /rules/:id/evaluate` → `{ evaluation }`. */
export async function evaluate(id, options) {
    const body = await post(`/rules/${id}/evaluate`, {}, options);
    return body.evaluation;
}
/** `GET /rules/evaluations` → `{ evaluations }`. */
export async function evaluations(query = {}, options) {
    const body = await get('/rules/evaluations', {
        ...options,
        query: { ...query },
    });
    return body.evaluations;
}
/** `GET /rules/:id/audit` → `{ audit }`. */
export async function audit(id, options) {
    const body = await get(`/rules/${id}/audit`, options);
    return body.audit;
}
/** `GET /rules/:id/alerts` → `{ alerts }` (inbox alerts this rule produced). */
export async function alerts(id, options) {
    const body = await get(`/rules/${id}/alerts`, options);
    return body.alerts;
}
/** `GET /rules/:id/executions` → `{ executions }` (business-outcome history). */
export async function executions(id, options) {
    const body = await get(`/rules/${id}/executions`, options);
    return body.executions;
}
//# sourceMappingURL=rules.js.map